import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { AutoCompleteCompleteEvent, AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { DropdownModule } from 'primeng/dropdown';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { SuperAdminService } from '../../services/super-admin/super-admin.service';
import { UtilityService } from '../../services/utility/utility.service';
import { SearchByNameResponse } from '../../models/account/searchByNameResponse';
import { StudentMentorRequestStatus, StudentMentorRequestStatusDescription } from '../../models/enums/studentMentorRequestStatus';
import { AllStudentMentorResponse } from '../../models/super-admin/allStudentMentorResponse';
import { Router } from '@angular/router';
import { ToastModule } from 'primeng/toast';

@Component({
  selector: 'app-student-mentor-requests',
  standalone: true,
  imports: [
      AutoCompleteModule, DropdownModule, CalendarModule, FormsModule, 
      ReactiveFormsModule, CommonModule, ButtonModule, TableModule, ToastModule],
  templateUrl: './student-mentor-requests.component.html',
  styleUrl: './student-mentor-requests.component.scss'
})
export class StudentMentorRequestsComponent {

  submitted: boolean = false;
  filterForm!: FormGroup;
  names:SearchByNameResponse[] = [];
  mentorNames:SearchByNameResponse[] = []
  students: AllStudentMentorResponse[] = [];
  statuses = [
    { name: StudentMentorRequestStatusDescription[StudentMentorRequestStatus.Pending], value: 0 },
    { name: StudentMentorRequestStatusDescription[StudentMentorRequestStatus.Approved], value: 1 },
    { name: StudentMentorRequestStatusDescription[StudentMentorRequestStatus.Rejected], value: 2 }
  ];

  isLoading: boolean = false;
  isSearching: boolean = false;
  total: number = 0;
  first: number = 0;
  rowSize: number = 10;

  isAccepting: boolean = false;
  isRejecting: boolean = false;
  isExportLoading: boolean = false;
  constructor(
    private fb: FormBuilder,
    private utilityService: UtilityService,
    private superAdminService: SuperAdminService,
    private messageService: MessageService,
    private router: Router
  ){}

  ngOnInit() {
    this.filterForm = this.fb.group({
      studentName:[null],        
      mentorName: [null],
      status: [null]
    });
    this.search(1);
  }

  viewProfile(id: string): void{
    this.router.navigate(['/admin','view-profile', id]);
  }

  filterNames(event: AutoCompleteCompleteEvent) {
    if (event.query.length > 2) {
      this.utilityService.searchNamesByRole({
        roleName: 'Student',
        searchText: event.query
      }).subscribe({
        next: (response) => {
          this.names = response.result
        }
      });
    } else {
      this.names = [];
    }
  }

  filterMentorNames(event: AutoCompleteCompleteEvent) {
    if (event.query.length > 2) {
      this.utilityService.searchNamesByRole({
        roleName: 'Mentor',
        searchText: event.query
      }).subscribe({
        next: (response) => {
          this.mentorNames = response.result
        }
      });
    } else {
      this.mentorNames = [];
    }
  }

  search(pageNumber: number){
    this.isSearching = true;
    this.superAdminService.getStudentMentorForSuperAdmin({
      studentName: this.filterForm.value.studentName,
      mentorName: this.filterForm.value.mentorName,
      status: this.filterForm.value.status,
      pageNumber: pageNumber,
      pageSize: this.rowSize
    }).subscribe({
      next: (response) => {
        this.students = response.result;
        this.total = response.total;
        this.isSearching = false;
      }
    })
  }

  acceptTestRequest(studentid: string, index: number){
    this.isAccepting = true;
    this.superAdminService.updateStudentMentorStatus({
      id: studentid,
      status: StudentMentorRequestStatus.Approved
    }).subscribe({
      next: () => {
        this.isAccepting = false;
        this.students[index].status = StudentMentorRequestStatus.Approved;
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Request accepted'});
      },
      error: () => {
        this.isAccepting = false;
        this.messageService.add({severity: 'error', summary: "Error", detail: 'Failed to accept request'});
      }
    })
  }
  rejectTestRequest(studentid: string, index: number){
    this.isRejecting = true;
    this.superAdminService.updateStudentMentorStatus({
      id: studentid,
      status: StudentMentorRequestStatus.Rejected
    }).subscribe({
      next: () => {
        this.isRejecting = false;
        this.students[index].status = StudentMentorRequestStatus.Rejected;
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Request rejected'});
      },
      error: () => {
        this.isRejecting = false;
        this.messageService.add({severity: 'error', summary: "Error", detail: 'Failed to reject request'});
      }
    })
  }

  onLazyLoad($event: TableLazyLoadEvent): void {
    const pageNumber = Math.floor(($event.first ?? 0) / ($event.rows ?? this.rowSize)) + 1;
    this.first = $event.first ?? 0;
    this.rowSize = $event.rows ?? this.rowSize;
    this.search(pageNumber);
  }

  applyFilters(): void {
    this.first = 0;
    this.search(1);
  }

  clearFilters(): void {
    this.filterForm.reset();
    this.first = 0;
    this.search(1);
  }

  downloadStudentMentorList(): void {
    this.isExportLoading = true;
    this.superAdminService.downloadStudentMentorList({
      StudentName: this.filterForm.value.studentName,
      MentorName: this.filterForm.value.mentorName,
      Status: this.filterForm.value.status,
      PageNumber: 1,
      PageSize: 2147483647
    }).subscribe({
      next: (resp) => {
        const cd = resp.headers.get('Content-Disposition');
        const filename = this.getFilenameFromContentDisposition(cd)
          || `Student_Mentor_Requests_${new Date().toISOString().replace(/[:.]/g, '-')}.xlsx`;
        const blob = new Blob([resp.body!], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        this.saveBlobAsFile(blob, filename);
        this.isExportLoading = false;
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'File downloaded successfully' });
      },
      error: (err) => {
        this.isExportLoading = false;
        console.error('Download failed', err);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to download file' });
      }
    });
  }

  private getFilenameFromContentDisposition(contentDisposition: string | null): string | null {
    if (contentDisposition && contentDisposition.indexOf('attachment') !== -1) {
      const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
      const matches = filenameRegex.exec(contentDisposition);
      if (matches != null && matches.length > 1) {
        return matches[1].replace(/['"]/g, '');
      }
    }
    return null;
  }

  private saveBlobAsFile(blob: Blob | null, filename: string): void {
    if (blob) {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      document.body.appendChild(a);
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    }
  }
}
